// src/components/premium/Paywall.jsx
//
// The purchase sheet. Opened by the `pph:open-paywall` event that every gate
// dispatches, so no gate needs to know this component exists.
//
// Two things here are review requirements, not niceties:
//
//   1. RESTORE PURCHASES. A non-consumable must be restorable, and App Review
//      checks. It is also the path a returning buyer takes on a new device.
//   2. The price shown is the STORE's localised string, never a hard-coded
//      "$4.99" — a buyer in Australia pays A$7.99, and showing them a US price
//      is both wrong and a rejection risk.
//
// Australian English throughout.

import React, { useCallback, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { X, Check, Loader2 } from 'lucide-react';
import { revenueCatService } from '../../services/revenueCatService';
import { modulesByLevel } from '../../data/modules';
import { MODULE_TOOLS } from '../../data/moduleTools';
import { courseValue, formatHours } from '../../data/accessModel';
import { showToast } from '../ui/toast';
import Button from '../shared/Button';

// What the purchase adds, measured from the content (accessModel.courseValue),
// so the figures stay true as modules grow.
const VALUE = courseValue(Object.values(modulesByLevel).flat(), MODULE_TOOLS);

const INCLUDED = [
  `${VALUE.paidModuleCount} more modules: about ${formatHours(VALUE.paidMinutes)} of the course`,
  `${VALUE.paidToolCount} more tools in the Toolkit`,
  'The Original Position’s group modes: Parlour, and hosting a linked table',
  'Yours for good: one payment, no subscription',
];

const Paywall = () => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null); // 'buy' | 'restore' | null
  const [price, setPrice] = useState(null);
  // Why it opened: { nextModuleTitle, finishedFree } from a gate or from finishing
  // the free modules, or nothing when opened from elsewhere.
  const [context, setContext] = useState(null);

  useEffect(() => {
    const onOpen = (event) => {
      setContext(event?.detail || null);
      setPrice(revenueCatService.getForeverPriceString());
      setOpen(true);
    };
    window.addEventListener('pph:open-paywall', onOpen);
    return () => window.removeEventListener('pph:open-paywall', onOpen);
  }, []);

  const close = useCallback(() => { if (!busy) setOpen(false); }, [busy]);

  // Escape closes, as any dialog should.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  const buy = async () => {
    setBusy('buy');
    const result = await revenueCatService.purchaseForever();
    setBusy(null);
    if (result.success) {
      showToast('Unlocked. Enjoy the whole course.');
      setOpen(false);
    } else if (!result.cancelled) {
      showToast(result.error || 'The purchase could not be completed.');
    }
  };

  const restore = async () => {
    setBusy('restore');
    const result = await revenueCatService.restorePurchases();
    setBusy(null);
    if (result.success && result.unlocked) {
      showToast('Restored. Welcome back.');
      setOpen(false);
    } else if (result.success) {
      showToast('No previous purchase was found on this Apple Account.');
    } else {
      showToast(result.error || 'Nothing could be restored.');
    }
  };

  if (!open) return null;

  // z-[60]: above the iOS tab bar (z-50), which otherwise sat over the bottom of
  // this sheet and hid Restore, the one control App Review always looks for. The
  // bottom padding keeps Restore clear of the home indicator.

  const native = Capacitor.isNativePlatform();

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="paywall-title"
      onClick={close}
    >
      <div
        className="w-full sm:max-w-md bg-charcoal border border-pearl/10 rounded-t-3xl sm:rounded-3xl p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] sm:p-8 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <h2 id="paywall-title" className="text-xl sm:text-2xl font-bold text-pearl">
              {context?.finishedFree
                ? 'You’ve finished the free modules'
                : context?.nextModuleTitle
                  ? `Continue with ${context.nextModuleTitle}`
                  : 'The whole of Profound Philosophy'}
            </h2>
            {context?.nextModuleTitle && (
              <p className="mt-1 text-sm text-pearl/75">
                {context.finishedFree
                  ? `Carry on with ${context.nextModuleTitle} and the rest of the course.`
                  : 'It’s part of the full course.'}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="p-2 -m-2 text-pearl/60 hover:text-pearl rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <ul className="space-y-3 mb-6">
          {INCLUDED.map((item) => (
            <li key={item} className="flex gap-3 text-sm sm:text-base text-pearl/85">
              <Check className="w-5 h-5 text-bright-gold shrink-0 mt-0.5" aria-hidden="true" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        {native ? (
          <>
            <Button size="medium" className="w-full" onClick={buy} disabled={Boolean(busy)}>
              {busy === 'buy' ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Just a moment…
                </span>
              ) : (
                price ? `Unlock for ${price}` : 'Unlock Profound Philosophy'
              )}
            </Button>

            <button
              type="button"
              onClick={restore}
              disabled={Boolean(busy)}
              className="w-full mt-3 py-2 text-sm text-pearl/70 hover:text-pearl underline disabled:opacity-60"
            >
              {busy === 'restore' ? 'Checking…' : 'Restore a previous purchase'}
            </button>
          </>
        ) : (
          <p className="text-sm text-pearl/70">
            The full course is bought in the Profound Philosophy app on iPhone. Buy it there
            while signed in to your Dive Deep Dialogues account, then sign in here with the
            same account and everything unlocks on the web too.
          </p>
        )}

        {/* On native, no mention of buying outside the store (App Store rule 3.1.1). */}

        <p className="mt-5 text-xs text-pearl/50">

          {native

            ? 'Already a Dive Deep Dialogues member? Sign in with the same account and your membership applies here.'

            : 'More of Sofia, beyond her free daily allowance, comes with a Dive Deep Dialogues membership rather than this one-off purchase.'}

        </p>
      </div>
    </div>
  );
};

export default Paywall;
